import { useState, useEffect } from 'react';
import api from '@/api';
import { Loader2, History, Filter, ChevronLeft, ChevronRight, Shield, User } from 'lucide-react';
import { toast } from 'sonner';

const EVENT_LABELS = {
  customer_invoice_generated: { label: 'Invoice Generated', color: 'text-[#57EB31]' },
  manual_invoice_created: { label: 'Manual Invoice Created', color: 'text-[#57EB31]' },
  customer_invoice_edited: { label: 'Invoice Edited', color: 'text-[#3B82F6]' },
  payment_status_changed: { label: 'Payment Updated', color: 'text-[#3B82F6]' },
  pdf_regenerated: { label: 'PDF Regenerated', color: 'text-cyan-400' },
  invoice_emailed: { label: 'Invoice Emailed', color: 'text-violet-400' },
  supplier_invoice_uploaded: { label: 'Supplier Invoice Uploaded', color: 'text-orange-400' },
  supplier_items_edited: { label: 'Items Edited', color: 'text-[#3B82F6]' },
  extraction_completed: { label: 'AI Extraction Done', color: 'text-[#57EB31]' },
  supplier_invoice_from_repair: { label: 'Invoice from Repair', color: 'text-cyan-400' },
  pricing_tier_created: { label: 'Pricing Tier Created', color: 'text-violet-400' },
};

export default function AuditHistory() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [eventTypes, setEventTypes] = useState([]);
  const [filterType, setFilterType] = useState('');
  const limit = 30;

  useEffect(() => {
    api.get('/ops-analytics/audit-log/event-types').then(r => setEventTypes(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [page, filterType]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { skip: page * limit, limit };
      if (filterType) params.event_type = filterType;
      const res = await api.get('/ops-analytics/audit-log', { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load audit log'); }
    finally { setLoading(false); }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-6 animate-fade-in" data-testid="audit-history-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#5C6A7A] mb-1">// ADMIN</p>
          <h1 className="text-white font-['Manrope'] font-extrabold text-2xl">Audit History</h1>
          <p className="text-[#5C6A7A] font-['IBM_Plex_Sans'] text-sm mt-0.5">Who did what — full activity log</p>
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-[#5C6A7A]" />
          <select value={filterType} onChange={e => { setFilterType(e.target.value); setPage(0); }}
            className="h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['JetBrains_Mono'] text-xs focus:outline-none focus:border-[#3B82F6]"
            data-testid="audit-filter-select">
            <option value="">All Events</option>
            {eventTypes.map(t => (
              <option key={t} value={t}>{EVENT_LABELS[t]?.label || t.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-[#1A2332] border border-[#2D3F5E] rounded-sm overflow-hidden" data-testid="audit-table">
        {/* Header */}
        <div className="grid grid-cols-12 gap-3 px-4 py-2.5 bg-[#0A0F1A] border-b border-[#2D3F5E]">
          <span className="col-span-2 text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A]">Time</span>
          <span className="col-span-2 text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A]">User</span>
          <span className="col-span-2 text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A]">Event</span>
          <span className="col-span-6 text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A]">Details</span>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="text-[#3B82F6] animate-spin" size={20} /></div>
        ) : items.length === 0 ? (
          <p className="text-center py-12 text-[#5C6A7A] font-['IBM_Plex_Sans'] text-sm">No audit events found</p>
        ) : (
          <div className="divide-y divide-[#2D3F5E]/30">
            {items.map(item => {
              const ev = EVENT_LABELS[item.event_type] || { label: item.event_type?.replace(/_/g, ' '), color: 'text-[#8B9AAB]' };
              return (
                <div key={item.id} className="grid grid-cols-12 gap-3 px-4 py-3 hover:bg-[#0A0F1A]/60 transition-colors"
                  data-testid={`audit-row-${item.id}`}>
                  <div className="col-span-2">
                    <p className="text-xs font-['JetBrains_Mono'] text-[#8B9AAB]">{formatTime(item.created_at)}</p>
                  </div>
                  <div className="col-span-2 flex items-center gap-2 min-w-0">
                    <div className="w-6 h-6 rounded-full bg-[#0A0F1A] border border-[#2D3F5E] flex items-center justify-center flex-shrink-0">
                      <User size={10} className="text-[#5C6A7A]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-['IBM_Plex_Sans'] text-white truncate">{item.user_name}</p>
                      <p className="text-[9px] font-['JetBrains_Mono'] text-[#5C6A7A] truncate">{item.user_role}</p>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <span className={`text-xs font-['JetBrains_Mono'] font-bold ${ev.color}`}>{ev.label}</span>
                  </div>
                  <div className="col-span-6">
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(item.details || {}).map(([k, v]) => (
                        <span key={k} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-[#0A0F1A] border border-[#2D3F5E]/50 text-[10px] font-['JetBrains_Mono']">
                          <span className="text-[#5C6A7A]">{k}:</span>
                          <span className="text-[#8B9AAB]">{typeof v === 'object' ? JSON.stringify(v) : String(v).slice(0, 40)}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 bg-[#0A0F1A] border-t border-[#2D3F5E]">
            <span className="text-xs font-['JetBrains_Mono'] text-[#5C6A7A]">{total} events total</span>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                className="p-1.5 border border-[#2D3F5E] rounded-sm text-[#5C6A7A] hover:text-[#3B82F6] hover:border-[#3B82F6]/50 disabled:opacity-30 transition-colors">
                <ChevronLeft size={14} />
              </button>
              <span className="text-xs font-['JetBrains_Mono'] text-[#8B9AAB]">{page + 1} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                className="p-1.5 border border-[#2D3F5E] rounded-sm text-[#5C6A7A] hover:text-[#3B82F6] hover:border-[#3B82F6]/50 disabled:opacity-30 transition-colors">
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
